import {
  Button,
  Input as ChakraInput,
  HStack,
  Image,
  ImageProps,
  InputLeftElementProps,
  InputProps,
  Select,
  StackProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import { jsx } from '@emotion/react';
import { ChangeEvent, cloneElement, forwardRef, useEffect, useMemo, useState } from 'react';
import CurrencyMaskInput from 'react-currency-masked-input';
import { UseFormRegisterReturn } from 'react-hook-form';
import { FaTimes } from 'react-icons/fa';
import InputMask from 'react-input-mask';
import { ShowPasswordToggle } from './ShowPasswordToggle';
import { toPhone } from '../../formatters';
import JSX = jsx.JSX;

export const Input = forwardRef(function (
  {
    leftElement,
    leftElementProps = {},
    rightElement,
    rightElementProps = {},
    onValueChange,
    onChange = (e) => onValueChange && onValueChange(e.target.value),
    register,
    label = '',
    ...props
  }: InputProps & IInputProps,
  ref
) {
  const animatedTextEffect = props.value || props.onBlur;
  const labelElement = useMemo(
    () => (
      <Text
        pos={'absolute'}
        transition={'0.2s'}
        fontWeight={500}
        height={'20px'}
        zIndex={100}
        color={animatedTextEffect ? 'blackAlpha.600' : 'blackAlpha.400'}
        top={animatedTextEffect ? '0px' : '4px'}
        left={animatedTextEffect ? '0px' : '5px'}
        fontSize={animatedTextEffect ? '12px' : '16px'}
      >
        {label}
      </Text>
    ),
    [animatedTextEffect, label]
  );

  return (
    <HStack
      backgroundColor={'#f9f9f9'}
      width={'100%'}
      padding={'7px 10px'}
      borderRadius={'10px'}
      position={'relative'}
      border="2px solid #d4d4d4"
    >
      {leftElement && cloneElement(leftElement, leftElementProps)}
      <VStack width={'100%'} pos={'relative'} spacing={0} align={'flex-start'}>
        {labelElement}
        <ChakraInput
          paddingTop={label ? (animatedTextEffect ? '18px' : '0px') : '0px'}
          border="0px"
          variant={'unstyled'}
          width={'100%'}
          minH={label ? (animatedTextEffect ? 'inherit' : '32px') : '30px'}
          ref={ref}
          {...register}
          onChange={onChange}
          fontSize={'16px'}
          {...props}
        />
      </VStack>

      {rightElement && cloneElement(rightElement, { fontSize: '26px', color: 'black', ...rightElementProps })}
    </HStack>
  );
});

export const MaskedInput = forwardRef(function (
  { mask = '', ...props }: { mask: string | undefined } & InputProps & IInputProps,
  ref
) {
  return <Input ref={ref} as={InputMask} mask={mask} {...props} />;
});

export const CPFInput = forwardRef(function (props: InputProps & IInputProps, ref) {
  return <MaskedInput ref={ref} mask={'999.999.999-99'} {...props} />;
});

export const CellphoneInput = forwardRef(function (props: InputProps & IInputProps, ref) {
  return <MaskedInput ref={ref} mask={'(99) 9 9999-9999'} {...props} />;
});

/** Telefone sem máscara fixa: aceita +55, 0800/800, fixo e celular (ver toPhone). */
export const PhoneInput = forwardRef(function ({ onChange, ...props }: InputProps & IInputProps, ref) {
  return (
    <Input
      ref={ref}
      type="tel"
      inputMode="tel"
      {...props}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        e.target.value = toPhone(e.target.value);
        onChange?.(e);
      }}
    />
  );
});

export const PostalCodeInput = forwardRef(function (props: InputProps & IInputProps, ref) {
  return <MaskedInput ref={ref} mask={'99999-999'} {...props} />;
});

export const CNPJInput = forwardRef(function (props: InputProps & IInputProps, ref) {
  return <MaskedInput ref={ref} mask={'99.999.999/9999-99'} {...props} />;
});

export const PasswordInput = forwardRef(function (props: InputProps & IInputProps, ref) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Input
      ref={ref}
      label="Senha"
      type={showPassword ? 'text' : 'password'}
      rightElement={<ShowPasswordToggle value={showPassword} onChange={setShowPassword} />}
      {...props}
    />
  );
});

export const CPFCNPJInput = forwardRef(function (
  props: InputProps & IInputProps & { forceType?: 'cpf' | 'cnpj' },
  ref
) {
  const [inputType, setInputType] = useState<'cpf' | 'cnpj'>('cpf');
  const mask = useMemo(() => (inputType === 'cpf' ? '999.999.999-99' : '99.999.999/0001-99'), [inputType]);

  return (
    <MaskedInput
      label={props.disabled ? 'CPF/CNPJ' : ''}
      leftElement={
        !props.disabled && (
          <VStack w={'95px'}>
            <Select
              variant={'unstyled'}
              borderColor={'#d4d4d4'}
              backgroundColor={'#f9f9f9'}
              left={'15px'}
              width={'70px'}
              height={'44px'}
              top="0px"
              position={'absolute'}
              borderRadius={'0px'}
              borderRight={'1px solid #d4d4d4'}
              onChange={(e) => setInputType(e.target.value)}
              value={inputType}
              disabled={props.disabled}
              color="blackAlpha.700"
              fontWeight={'bold'}
              fontSize={'14px'}
            >
              <option value={'cpf'}>CPF</option>
              <option value={'cnpj'}>CNPJ</option>
            </Select>
          </VStack>
        )
      }
      ref={ref}
      mask={mask}
      {...props}
    />
  );
});

export const CurrencyInput = forwardRef(function (props: InputProps & IInputProps, ref) {
  return <Input ref={ref} leftElement={<Text>R$</Text>} as={CurrencyMaskInput} {...props} />;
});

export function ImageInput({
  value,
  onImageChange,
  imageProps,
  ...props
}: StackProps & {
  value: File | undefined;
  imageProps?: ImageProps;
  onImageChange: (value: File | undefined) => void;
}) {
  const [imageUrl, setImageUrl] = useState<string>();

  function onClick() {
    const input: HTMLInputElement = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.position = 'fixed';
    input.style.zIndex = '-1';

    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;

      if (!target || !target.files) return;

      const image = target.files[0];

      onImageChange(image);
    };

    document.body.appendChild(input);
    input.click();
  }

  useEffect(() => {
    if (!value) {
      setImageUrl(undefined);
      return;
    }

    const reader = new FileReader();

    reader.onloadend = function () {
      setImageUrl(reader.result as string);
    };

    reader.readAsDataURL(value);
  }, [value]);

  return (
    <VStack {...props} width="100%" position="relative">
      {value && (
        <FaTimes
          style={{
            cursor: 'pointer',
            position: 'absolute',
            right: '0px',
            top: '0px',
          }}
          onClick={() => onImageChange(undefined)}
        />
      )}
      {imageUrl && <Image {...imageProps} src={imageUrl} />}
      <Button width="100%" colorScheme="blue" onClick={onClick}>
        {value ? 'Trocar' : 'Adicionar'}
      </Button>
    </VStack>
  );
}

interface IInputProps {
  register?: UseFormRegisterReturn;
  leftElement?: JSX.Element;
  leftElementProps?: StackProps;
  rightElement?: JSX.Element;
  label?: string;
  rightElementProps?: InputLeftElementProps;
  onValueChange?: (value: string) => void;
  onInputChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  mask?: string;
  maskChar?: string | null;
  disabled?: boolean;
}
